import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { searchAllPayers } from '../../../api/claimsClient';
import Input from './Input';
import Icon from './Icon';

const SEARCH_DEBOUNCE_MS = 250;

/**
 * @param {{
 *   value: string|null,
 *   payerSnapshot: object|null,
 *   onSelect: (payer: object|null) => void,
 *   density?: 'default'|'paper',
 *   disabled?: boolean,
 * }} props
 */
export default function PayerPicker({
  value, payerSnapshot, onSelect,
  density = 'default', disabled = false,
}) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  /** @type {[any[], (v: any[]) => void]} */
  const [results, setResults] = useState(/** @type {any[]} */ ([]));
  /** @type {any} */
  const inputRef = useRef(null);
  /** @type {[any, (v: any) => void]} */
  const [rect, setRect] = useState(/** @type {any} */ (null));

  const showChip = value && payerSnapshot;

  useEffect(() => {
    if (!open) return undefined;
    const id = setTimeout(() => {
      // Searches Office Ally AND Claim.MD, deduplicated by payer_id — a payer
      // reachable only through Claim.MD was previously unselectable here.
      searchAllPayers(query).then(setResults).catch(() => setResults([]));
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [query, open]);

  useEffect(() => {
    if (!open) return undefined;
    function update() {
      if (inputRef.current) setRect(inputRef.current.getBoundingClientRect());
    }
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    function onDoc(/** @type {any} */ e) {
      if (inputRef.current && !inputRef.current.contains(e.target) && !e.target.closest?.('[data-picker-dropdown]')) {
        setOpen(false);
      }
    }
    window.addEventListener('mousedown', onDoc);
    return () => window.removeEventListener('mousedown', onDoc);
  }, [open]);

  if (showChip) {
    /** @type {any} */
    const snap = payerSnapshot;
    return (
      <div style={{
        display: 'inline-flex', alignItems: 'center', gap: 8,
        padding: '6px 10px',
        border: '1px solid var(--ab-fg)',
        borderRadius: 'var(--ab-radius-sm)',
        background: 'var(--ab-bg)',
        fontSize: 'var(--ab-text-sm)',
      }}>
        <span>{snap.name} · {snap.payer_id}</span>
        {!disabled && (
          <button
            type="button" aria-label="Clear payer"
            onClick={() => onSelect(null)}
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', display: 'inline-flex' }}
          >
            <Icon name="x" size={14} />
          </button>
        )}
      </div>
    );
  }

  return (
    <>
      <div ref={inputRef}>
        <Input
          placeholder="Search payer by name or ID"
          value={query}
          disabled={disabled}
          onFocus={() => !disabled && setOpen(true)}
          onChange={(/** @type {any} */ e) => setQuery(e.target.value)}
        />
      </div>
      {open && rect && createPortal(
        <div
          data-picker-dropdown
          style={{
            position: 'fixed', top: rect.bottom + 4, left: rect.left, width: rect.width,
            maxHeight: 320, overflow: 'auto',
            background: 'var(--ab-bg)', border: '1px solid var(--ab-fg)',
            borderRadius: 'var(--ab-radius-sm)', boxShadow: 'var(--ab-shadow-md)', zIndex: 1200,
          }}
        >
          {results.map((/** @type {any} */ p) => (
            <button
              key={p.payer_id}
              type="button"
              onClick={() => { onSelect(p); setOpen(false); setQuery(''); }}
              style={{
                display: 'block', width: '100%', textAlign: 'left',
                padding: '8px 12px', background: 'transparent',
                color: 'var(--ab-fg)',
                border: 'none', cursor: 'pointer',
                borderBottom: '1px solid var(--ab-border-hair)',
              }}
            >
              <div style={{ fontSize: 'var(--ab-text-sm)', fontWeight: 'var(--ab-weight-semi)' }}>{p.name}</div>
              <div style={{ fontSize: 'var(--ab-text-xs)', color: 'var(--ab-fg-subtle)' }}>
                <span data-testid="payer-id">{p.payer_id}</span>
                {p.address_line_1 ? ` · ${p.address_line_1}` : ''}
              </div>
            </button>
          ))}
          {results.length === 0 && !query.trim() && (
            <div style={{ padding: '10px 12px', fontSize: 'var(--ab-text-sm)', color: 'var(--ab-fg-subtle)' }}>
              Start typing to search.
            </div>
          )}
          {results.length === 0 && query.trim() && (
            <button
              type="button"
              onClick={() => {
                onSelect({ payer_id: '__custom__', name: query.trim(), custom: true });
                setOpen(false); setQuery('');
              }}
              style={{
                display: 'block', width: '100%', textAlign: 'left',
                padding: '10px 12px',
                background: 'var(--ab-bg-alt)',
                color: 'var(--ab-fg)',
                border: 'none', cursor: 'pointer',
                fontSize: 'var(--ab-text-sm)',
              }}
            >
              Use "{query}" as custom payer
            </button>
          )}
        </div>,
        document.body,
      )}
    </>
  );
}
